/**
 * Campaign Manager Module
 * Quản lý campaigns và promotions
 */

import * as StateManager from './state-manager.js';
import { formatPrice, escapeHtml, stripHtml } from './utils.js';
import { clearSubmittedHash } from './storage-service.js';

// Dependencies
let displayTotalsWithDiscount, updateTotals;

export function injectDependencies(deps) {
    displayTotalsWithDiscount = deps.displayTotalsWithDiscount;
    updateTotals = deps.updateTotals;
}

/**
 * Load promotional campaigns from ERPNext based on order total
 */
export function loadPromotionalCampaigns(orderTotal) {
    console.log('loadPromotionalCampaigns called with orderTotal:', orderTotal);

    const campaignsCache = StateManager.getCampaignsCache();
    const currentCampaignsTotal = StateManager.getCurrentCampaignsTotal();
    const currentItemCount = StateManager.getCurrentCampaignsItemCount();
    const selectedCourses = StateManager.getSelectedCourses();
    const newItemCount = selectedCourses.length;

    // Don't reload if BOTH total AND item count haven't changed
    // FIX: Must check item count too, because campaigns like "Mua 2 Tặng 1" depend on exact item count
    const totalUnchanged = Math.abs(orderTotal - currentCampaignsTotal) < 0.01;
    const itemCountUnchanged = newItemCount === currentItemCount;

    if (totalUnchanged && itemCountUnchanged && campaignsCache) {
        console.log('Using cached campaigns data (total and item count unchanged)');
        return;
    }

    if (orderTotal <= 0) {
        console.log('Order total is 0, hiding campaigns');
        jQuery('#ktvhvn-campaigns-container').hide();
        StateManager.setCampaignsCache(null);
        StateManager.setCurrentCampaignsTotal(0);
        StateManager.setCurrentCampaignsItemCount(0);
        return;
    }

    // DEBOUNCE: Clear previous timer and wait 500ms before making API call
    const campaignLoadTimeout = StateManager.getCampaignLoadTimeout();
    clearTimeout(campaignLoadTimeout);
    const newTimeout = setTimeout(function() {
        loadPromotionalCampaignsActual(orderTotal);
    }, 500);
    StateManager.setCampaignLoadTimeout(newTimeout);
}

/**
 * Actual API call function (called after debounce delay)
 */
function loadPromotionalCampaignsActual(orderTotal) {
    StateManager.setCurrentCampaignsTotal(orderTotal);
    // Also save item count for cache invalidation
    const selectedCourses = StateManager.getSelectedCourses();
    StateManager.setCurrentCampaignsItemCount(selectedCourses.length);

    // Get customer email if available
    const customerEmail = jQuery('#ktvhvn-email').val().trim();

    // Collect selected courses with their details
    const items = [];

    // Build items array from selectedCourses (already loaded above)
    selectedCourses.forEach(function(course) {
        items.push({
            product_id: course.product_id,
            variation_id: course.selected_variation ? course.selected_variation.variation_id : 0,
            price: course.final_price,
            name: course.name
        });
    });

    console.log('Fetching campaigns from server. Order value:', orderTotal, 'Email:', customerEmail, 'Items:', items.length);

    // Make AJAX request to get campaigns
    jQuery.post(window.ktvhvnCourseReg.ajax_url, {
        action: 'ktvhvn_get_campaigns',
        nonce: window.ktvhvnCourseReg.nonce,
        order_value: orderTotal,
        customer_email: customerEmail,
        items: items
    })
    .done(function(response) {
        console.log('AJAX response received:', response);
        if (response.success && response.data.campaigns && response.data.campaigns.length > 0) {
            console.log('Campaigns found:', response.data.campaigns.length);
            StateManager.setCampaignsCache(response.data);
            displayCampaigns(response.data);
        } else {
            console.log('No campaigns available or response not successful');
            jQuery('#ktvhvn-campaigns-container').hide();
            StateManager.setCampaignsCache(null);
        }
    })
    .fail(function(xhr, status, error) {
        console.error('Failed to load promotional campaigns');
        console.error('Status:', status);
        console.error('Error:', error);
        console.error('Response:', xhr.responseText);
        jQuery('#ktvhvn-campaigns-container').hide();
        StateManager.setCampaignsCache(null);
    });
}

/**
 * Display campaigns in the UI - Show banner slider only
 */
export function displayCampaigns(campaignsData) {
    // Display banner slider at the top
    displayCampaignsBannerSlider(campaignsData);

    const selectedCampaignId = StateManager.getSelectedCampaignId();
    const discountPercent = StateManager.getDiscountPercent();
    const currentCampaignsTotal = StateManager.getCurrentCampaignsTotal();
    const freeCoursesDiscount = StateManager.getFreeCoursesDiscount();

    // ✅ FREE COURSES: Count paid and free courses
    const selectedCourses = StateManager.getSelectedCourses();
    let paidCoursesCount = 0;
    let freeCoursesCount = 0;
    selectedCourses.forEach(course => {
        if (course.isFreeCourse) {
            freeCoursesCount++;
        } else {
            paidCoursesCount++;
        }
    });

    // Auto-select best campaign if none selected (not explicitly deselected) and it's applicable
    // Use applicable_campaigns to find best applicable campaign
    const applicableCampaigns = campaignsData.applicable_campaigns ||
        (campaignsData.campaigns || []).filter(c => c.is_applicable);

    // Find best applicable campaign (highest discount_amount)
    let bestApplicableCampaign = null;
    if (applicableCampaigns.length > 0) {
        bestApplicableCampaign = applicableCampaigns.reduce((best, current) => {
            return (parseFloat(current.discount_amount) || 0) > (parseFloat(best.discount_amount) || 0) ? current : best;
        }, applicableCampaigns[0]);
    }

    // FIX: Check if currently selected campaign is still applicable
    // If not applicable anymore, reset to best available campaign
    if (selectedCampaignId !== null && selectedCampaignId !== 'none') {
        const isCurrentCampaignApplicable = applicableCampaigns.some(c => c.id === selectedCampaignId);
        if (!isCurrentCampaignApplicable) {
            // Current campaign no longer applicable (e.g., user removed courses)
            // Reset to null so it will auto-select best campaign
            StateManager.setSelectedCampaignId(null);
        }
    }

    // ✅ AUTO-SELECT "Mua 1 Tặng 1": When exactly 1 paid + 1 free course
    if (paidCoursesCount === 1 && freeCoursesCount === 1) {
        const mua1Tang1 = (campaignsData.campaigns || []).find(c => c.id === 'mua-1-tang-1');
        if (mua1Tang1) {
            StateManager.setSelectedCampaignId('mua-1-tang-1');
        }
    } else if (StateManager.getSelectedCampaignId() === null && bestApplicableCampaign) {
        // Auto-select best campaign if none selected
        StateManager.setSelectedCampaignId(bestApplicableCampaign.id);
    }

    // UPDATE: Refresh totals with accurate discount from API
    const campaignDiscount = getCampaignDiscount();
    let campaignName = '';
    let finalDiscount = 0;

    // ✅ FREE COURSES: Calculate total discount = freeCoursesDiscount + campaign additional discount
    if (freeCoursesDiscount > 0) {
        finalDiscount = freeCoursesDiscount;

        // Get selected campaign info
        const currentSelectedId = StateManager.getSelectedCampaignId();
        const selectedCampaign = currentSelectedId && campaignsData.campaigns
            ? campaignsData.campaigns.find(c => c.id === currentSelectedId)
            : null;

        if (selectedCampaign) {
            campaignName = selectedCampaign.name;

            // If campaign is free_item type with additional percentage discount
            if (selectedCampaign.discount_type === 'free_item' && parseFloat(selectedCampaign.discount_value) > 0) {
                const additionalPercent = parseFloat(selectedCampaign.discount_value);
                const paidTotal = currentCampaignsTotal - freeCoursesDiscount;
                const additionalDiscount = paidTotal * additionalPercent / 100;
                finalDiscount += additionalDiscount;
            }
        } else if (paidCoursesCount === 1 && freeCoursesCount === 1) {
            campaignName = 'Mua 1 Tặng 1';
        } else {
            campaignName = 'Khóa học miễn phí';
        }
    } else if (campaignDiscount > 0 && campaignsData.campaigns) {
        finalDiscount = campaignDiscount;
        const selectedCampaign = campaignsData.campaigns.find(c => c.id === StateManager.getSelectedCampaignId());
        if (selectedCampaign) {
            campaignName = selectedCampaign.name;
        }
    } else if (discountPercent > 0) {
        finalDiscount = currentCampaignsTotal * discountPercent / 100;
        campaignName = 'Giảm ' + discountPercent + '% cho đơn từ 5 khóa';
    }

    if (finalDiscount > 0) {
        displayTotalsWithDiscount(currentCampaignsTotal, finalDiscount, campaignName);
    }

    console.log('displayCampaigns called - banner slider displayed, totals updated with accurate discount');
}

/**
 * Display campaigns as banner slider at the top
 */
function displayCampaignsBannerSlider(campaignsData) {
    const bannerSlider = jQuery('#ktvhvn-campaigns-banner-slider');
    const bannerTrack = jQuery('#ktvhvn-campaigns-banner-track');
    const dotsContainer = jQuery('#ktvhvn-campaigns-banner-dots');
    const selectedCampaignId = StateManager.getSelectedCampaignId();

    console.log('displayCampaignsBannerSlider called with data:', campaignsData);
    console.log('Total campaigns:', campaignsData.campaigns ? campaignsData.campaigns.length : 0);

    if (!campaignsData.campaigns || campaignsData.campaigns.length === 0) {
        bannerSlider.hide();
        return;
    }

    // Build banner HTML
    let bannersHtml = '';
    let dotsHtml = '';

    campaignsData.campaigns.forEach(function(campaign, index) {
        // Handle all 3 discount types
        let discountText;
        if (campaign.discount_type === 'percentage') {
            discountText = campaign.discount_value + '%';
        } else if (campaign.discount_type === 'fixed_amount') {
            discountText = formatPrice(campaign.discount_value);
        } else if (campaign.discount_type === 'free_item') {
            if (campaign.discount_value > 0) {
                discountText = campaign.discount_value + '%';
            } else {
                discountText = 'Quà tặng';
            }
        } else {
            discountText = formatPrice(campaign.discount_value);
        }

        const isSelected = selectedCampaignId === campaign.id;
        const badgeHtml = isSelected ? '<div style="position: absolute; top: 10px; left: 10px; background: #4caf50; color: white; padding: 5px 15px; border-radius: 20px; font-size: 12px; font-weight: bold; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">✓ ĐANG ÁP DỤNG</div>' : '';

        bannersHtml += '<div class="ktvhvn-campaign-banner" style="min-width: 100%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; padding: 20px 30px; position: relative; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.1);" data-campaign-id="' + campaign.id + '">';
        bannersHtml += badgeHtml;
        bannersHtml += '<div style="display: flex; justify-content: space-between; align-items: center; gap: 20px;">';
        bannersHtml += '<div style="flex: 1; color: white;">';
        bannersHtml += '<div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">';
        bannersHtml += '<span style="font-size: 24px;">🎁</span>';
        bannersHtml += '<h3 style="margin: 0; font-size: 20px; font-weight: 700;">' + escapeHtml(campaign.name) + '</h3>';
        bannersHtml += '</div>';
        if (campaign.description) {
            bannersHtml += '<p style="margin: 0; font-size: 14px; opacity: 0.95;">' + stripHtml(campaign.description) + '</p>';
        }
        if (campaign.min_order_value > 0) {
            bannersHtml += '<div style="margin-top: 8px; font-size: 13px; opacity: 0.9;">Đơn hàng tối thiểu: <strong>' + formatPrice(campaign.min_order_value) + '</strong></div>';
        }
        bannersHtml += '</div>';
        bannersHtml += '<div style="background: rgba(255,255,255,0.2); padding: 20px 30px; border-radius: 10px; text-align: center; min-width: 120px;">';
        bannersHtml += '<div style="font-size: 32px; font-weight: 900; line-height: 1; color: white;">' + discountText + '</div>';
        bannersHtml += '<div style="font-size: 12px; opacity: 0.9; margin-top: 6px; text-transform: uppercase; color: white;">GIẢM GIÁ</div>';
        bannersHtml += '</div>';
        bannersHtml += '</div>';
        bannersHtml += '</div>';

        dotsHtml += '<span class="ktvhvn-banner-dot" data-index="' + index + '" style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ' + (index === 0 ? '#667eea' : 'rgba(255,255,255,0.5)') + '; margin: 0 4px; cursor: pointer; transition: all 0.3s;"></span>';
    });

    bannerTrack.html(bannersHtml);
    dotsContainer.html(dotsHtml);
    bannerSlider.show();

    // Initialize slider
    initializeBannerSlider(campaignsData);
}

/**
 * Initialize banner slider with auto-slide functionality
 */
function initializeBannerSlider(campaignsData) {
    const bannerSlider = jQuery('#ktvhvn-campaigns-banner-slider');
    const bannerTrack = jQuery('#ktvhvn-campaigns-banner-track');
    const totalSlides = campaignsData.campaigns.length;
    let currentSlide = 0;
    let autoSlideInterval;

    function goToSlide(index) {
        currentSlide = index;
        const offset = -100 * index;
        bannerTrack.css('transform', 'translateX(' + offset + '%)');

        jQuery('.ktvhvn-banner-dot').css('background', 'rgba(255,255,255,0.5)');
        jQuery('.ktvhvn-banner-dot[data-index="' + index + '"]').css('background', '#667eea');
    }

    function startAutoSlide() {
        autoSlideInterval = setInterval(function() {
            currentSlide = (currentSlide + 1) % totalSlides;
            goToSlide(currentSlide);
        }, 5000);
    }

    bannerSlider.on('mouseenter', function() {
        clearInterval(autoSlideInterval);
    });

    bannerSlider.on('mouseleave', function() {
        startAutoSlide();
    });

    jQuery('.ktvhvn-banner-dot').on('click', function() {
        const index = parseInt(jQuery(this).data('index'));
        goToSlide(index);
    });

    jQuery('.ktvhvn-campaign-banner').on('click', function() {
        showAllCampaignsPopup(campaignsData);
    });

    // Touch swipe support
    let touchStartX = 0;
    let touchEndX = 0;

    bannerSlider.on('touchstart', function(e) {
        touchStartX = e.touches[0].clientX;
    });

    bannerSlider.on('touchend', function(e) {
        touchEndX = e.changedTouches[0].clientX;
        handleSwipe();
    });

    function handleSwipe() {
        if (touchEndX < touchStartX - 50) {
            currentSlide = (currentSlide + 1) % totalSlides;
            goToSlide(currentSlide);
        }
        if (touchEndX > touchStartX + 50) {
            currentSlide = (currentSlide - 1 + totalSlides) % totalSlides;
            goToSlide(currentSlide);
        }
    }

    startAutoSlide();
}

/**
 * Show popup with all campaigns
 */
export function showAllCampaignsPopup(campaignsData) {
    console.log('showAllCampaignsPopup called');

    // Remove old popup if exists
    jQuery('#ktvhvn-campaigns-popup-overlay').remove();

    const selectedCampaignId = StateManager.getSelectedCampaignId();
    let html = '<div class="ktvhvn-campaigns-popup-overlay" id="ktvhvn-campaigns-popup-overlay" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 999999; display: flex; align-items: center; justify-content: center; padding: 20px;">';
    html += '<div class="ktvhvn-campaigns-popup-content" style="background: white; max-width: 600px; width: 100%; max-height: 80vh; overflow-y: auto; border-radius: 8px; padding: 20px; position: relative;">';

    // Header
    html += '<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 1px solid #ddd;">';
    html += '<h3 style="margin: 0; color: #333; font-size: 18px; font-weight: 600;">Chọn Chương Trình Khuyến Mại</h3>';
    html += '<button class="ktvhvn-campaigns-popup-close" style="background: none; border: none; font-size: 24px; cursor: pointer; color: #999; line-height: 1; padding: 0; width: 30px; height: 30px;">&times;</button>';
    html += '</div>';

    html += '<div class="ktvhvn-campaigns-list" style="display: flex; flex-direction: column; gap: 12px;">';

    // Find best APPLICABLE campaign (not just best_discount which may not be applicable)
    const applicableCampaigns = campaignsData.applicable_campaigns ||
        (campaignsData.campaigns || []).filter(c => c.is_applicable);

    let bestApplicableCampaignId = null;
    if (applicableCampaigns.length > 0) {
        const bestApplicable = applicableCampaigns.reduce((best, current) => {
            return (parseFloat(current.discount_amount) || 0) > (parseFloat(best.discount_amount) || 0) ? current : best;
        }, applicableCampaigns[0]);
        bestApplicableCampaignId = bestApplicable.id;
    }

    campaignsData.campaigns.forEach(function(campaign, index) {
        const isSelected = selectedCampaignId === campaign.id;
        const isBest = bestApplicableCampaignId && campaign.id === bestApplicableCampaignId;
        const isApplicable = campaign.is_applicable;
        const uiHint = campaign.ui_hint || '';

        let discountText;
        if (campaign.discount_type === 'percentage') {
            discountText = 'Giảm ' + campaign.discount_value + '%';
        } else if (campaign.discount_type === 'fixed_amount') {
            discountText = 'Giảm ' + formatPrice(campaign.discount_value);
        } else if (campaign.discount_type === 'free_item') {
            if (campaign.discount_value > 0) {
                discountText = 'Giảm ' + campaign.discount_value + '%';
            } else {
                discountText = 'Quà tặng';
            }
        } else {
            discountText = 'Giảm ' + formatPrice(campaign.discount_value);
        }

        const borderColor = isSelected ? '#4caf50' : (isApplicable ? '#ddd' : '#f0f0f0');
        const bgColor = isSelected ? '#f0f9f4' : (isApplicable ? '#fff' : '#fafafa');
        const opacity = isApplicable ? '1' : '0.6';
        const cursor = isApplicable ? 'pointer' : 'not-allowed';

        html += '<label style="display: block; border: 2px solid ' + borderColor + '; background: ' + bgColor + '; padding: 15px; border-radius: 6px; cursor: ' + cursor + '; opacity: ' + opacity + ';">';
        html += '<div style="display: flex; align-items: flex-start; gap: 12px;">';
        html += '<input type="radio" name="ktvhvn-campaign" value="' + campaign.id + '" ' + (isSelected ? 'checked' : '') + ' ' + (!isApplicable ? 'disabled' : '') + ' class="ktvhvn-campaign-radio" data-campaign-id="' + campaign.id + '" style="margin-top: 4px; width: 18px; height: 18px; cursor: ' + cursor + ';">';
        html += '<div style="flex: 1;">';
        html += '<div style="font-size: 15px; font-weight: 600; color: #333; margin-bottom: 6px;">';
        html += escapeHtml(campaign.name);
        if (isBest && isApplicable) {
            html += ' <span style="color: #ff9800; font-size: 13px;">(Tiết kiệm nhất)</span>';
        }
        html += '</div>';
        html += '<div style="font-size: 14px; color: #4caf50; font-weight: 600; margin-bottom: 6px;">' + discountText + '</div>';
        if (campaign.description) {
            html += '<div style="font-size: 13px; color: #666; margin-bottom: 6px;">' + stripHtml(campaign.description) + '</div>';
        }
        if (uiHint) {
            html += '<div style="font-size: 13px; color: #f57c00; margin-top: 8px;">' + escapeHtml(uiHint) + '</div>';
        } else if (isApplicable) {
            const reqs = [];
            if (campaign.min_items > 0) {
                reqs.push('Tối thiểu ' + campaign.min_items + ' khóa');
            }
            if (campaign.min_order_value > 0) {
                reqs.push('Đơn tối thiểu ' + formatPrice(campaign.min_order_value));
            }
            if (reqs.length > 0) {
                html += '<div style="font-size: 12px; color: #999; margin-top: 6px;">✓ ' + reqs.join(', ') + '</div>';
            }
        }
        html += '</div>';
        html += '</div>';
        html += '</label>';
    });

    html += '</div>';
    html += '</div>';
    html += '</div>';

    jQuery('body').append(html);

    // Bind events
    jQuery('.ktvhvn-campaigns-popup-close').on('click', function() {
        jQuery('#ktvhvn-campaigns-popup-overlay').fadeOut(200, function() {
            jQuery(this).remove();
        });
    });

    jQuery('.ktvhvn-campaigns-popup-overlay').on('click', function(e) {
        if (e.target === this) {
            jQuery('#ktvhvn-campaigns-popup-overlay').fadeOut(200, function() {
                jQuery(this).remove();
            });
        }
    });

    jQuery(document).off('change', '.ktvhvn-campaign-radio').on('change', '.ktvhvn-campaign-radio', function() {
        const campaignId = jQuery(this).val();
        if (campaignId) {
            selectCampaign(campaignId, campaignsData);
        } else {
            deselectCampaign(campaignsData);
        }
        jQuery('#ktvhvn-campaigns-popup-overlay').fadeOut(200, function() {
            jQuery(this).remove();
        });
    });

    jQuery('#ktvhvn-campaigns-popup-overlay').hide().fadeIn(200);
}

/**
 * Select a specific campaign
 */
export function selectCampaign(campaignId, campaignsData) {
    StateManager.setSelectedCampaignId(campaignId);
    displayCampaigns(campaignsData);
    updateTotals();
    clearSubmittedHash();
}

/**
 * Deselect current campaign
 */
export function deselectCampaign(campaignsData) {
    StateManager.setSelectedCampaignId('none');
    displayCampaigns(campaignsData);
    updateTotals();
    clearSubmittedHash();
}

/**
 * Apply best campaign automatically
 */
export function applyBestCampaign() {
    console.log('applyBestCampaign called');

    const campaignsCache = StateManager.getCampaignsCache();
    if (!campaignsCache) {
        alert('Không có chương trình khuyến mại nào phù hợp');
        return;
    }

    // Find best APPLICABLE campaign
    const applicableCampaigns = campaignsCache.applicable_campaigns ||
        (campaignsCache.campaigns || []).filter(c => c.is_applicable);

    if (applicableCampaigns.length === 0) {
        alert('Không có chương trình khuyến mại nào đủ điều kiện áp dụng');
        return;
    }

    const bestCampaign = applicableCampaigns.reduce((best, current) => {
        return (parseFloat(current.discount_amount) || 0) > (parseFloat(best.discount_amount) || 0) ? current : best;
    }, applicableCampaigns[0]);

    selectCampaign(bestCampaign.id, campaignsCache);

    let discountText;
    if (bestCampaign.discount_type === 'percentage') {
        discountText = bestCampaign.discount_value + '%';
    } else if (bestCampaign.discount_type === 'fixed_amount') {
        discountText = formatPrice(bestCampaign.discount_value);
    } else if (bestCampaign.discount_type === 'free_item') {
        if (bestCampaign.discount_value > 0) {
            discountText = bestCampaign.discount_value + '%';
        } else {
            discountText = 'Quà tặng';
        }
    } else {
        discountText = formatPrice(bestCampaign.discount_value);
    }

    alert('Đã áp dụng chương trình khuyến mại tốt nhất!\n\n' + bestCampaign.name + '\nGiảm: ' + discountText);
}

/**
 * Get discount for selected campaign
 */
export function getCampaignDiscount() {
    const selectedCampaignId = StateManager.getSelectedCampaignId();
    const campaignsCache = StateManager.getCampaignsCache();

    if (!selectedCampaignId || selectedCampaignId === 'none' || !campaignsCache || !campaignsCache.campaigns) {
        return 0;
    }

    const selectedCampaign = campaignsCache.campaigns.find(c => c.id === selectedCampaignId);
    if (!selectedCampaign) {
        return 0;
    }

    return parseFloat(selectedCampaign.discount_amount) || 0;
}
